import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faRunning,
  faChalkboardTeacher,
  faCalendarAlt,
  faClock,
  faRuler,
  faMountain,
  faHourglassHalf,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import { InputField } from "../common/InputField";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import axios from "axios";
import Iframe from "react-iframe";
import { MandatoryGears } from "../components/MandatoryGears";
import { RaceInfo } from "../components/RaceInfo";
import { Rundown } from "../components/Rundown";

import bghero11 from "../assets/bg/bghero11.jpg";

import iconUtmb from "../assets/icons/utmb.png";
import iconItra0 from "../assets/icons/itra0.svg";
import iconUtmb20 from "../assets/icons/utmb20.png";
import iconItra1 from "../assets/icons/itra1.png";
import iconUtmb50 from "../assets/icons/utmb50.png";
import iconItra2 from "../assets/icons/itra2.png";
import iconItra3 from "../assets/icons/itra3.svg";
import iconUtmb100 from "../assets/icons/utmb100.svg";
import iconItra4 from "../assets/icons/itra4.png";

const SkeletonLoader = () => (
  <div className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-slate-100">
    <div className="relative z-0 container mx-auto px-4">
      <div className="w-full h-[60vh] bg-gray-200 animate-pulse"></div>
      <div className="p-4 space-y-4 mt-8">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not yet determined";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString. seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (! isNaN(date.getTime())) {
      return date. toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not yet determined";
  } catch {
    return "Date not yet determined";
  }
};

const formatPrice = (price, foreignerPrice) => {
  const domesticPrice =
    price != null && price > 0
      ? `Rp ${Number(price).toLocaleString("id-ID")} `
      : "Free";
  const foreignPrice =
    foreignerPrice != null && foreignerPrice > 0
      ?  `Rp ${Number(foreignerPrice).toLocaleString("id-ID")} (Foreigner)`
      : "Free";
  return `${domesticPrice} / ${foreignPrice}`;
};

const splitDescription = (description) => {
  if (!description) return ["No description available.", ""];
  const words = description.split(" ");
  const midPoint = Math.ceil(words.length / 2);
  const firstHalf = words.slice(0, midPoint). join(" ");
  const secondHalf = words.slice(midPoint).join(" ");
  return [firstHalf || "No description available.", secondHalf];
};

const getCategoryIcons = (categoryName, distance) => {
  switch (categoryName) {
    case "MOUNTAIN UPHILL (5K)":
      return {
        icon1Url: null,
        icon2Url: null,
        icon3Url: null,
      };
    case "MOUNTAIN CLASSIC (10K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb20,
        icon3Url: iconItra1,
      };
    case "SHORT TRAIL (42K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb50,
        icon3Url: iconItra3,
      };
    case "LONG TRAIL (75 K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb100,
        icon3Url: iconItra4,
      };
    default:
      return { icon1Url: null, icon2Url: null, icon3Url: null };
  }
};

const getCategorySlug = (categoryName) => {
  switch (categoryName) {
    case "MOUNTAIN UPHILL (5K)":
      return "5-vertical";
    case "MOUNTAIN CLASSIC (10K)":
      return "10-classic";
    case "SHORT TRAIL (42K)":
      return "42-short";
    case "LONG TRAIL (75 K)":
      return "75-long";
    default:
      return categoryName. toLowerCase(). replace(/\s+/g, "-");
  }
};

const isEventInFuture = (eventDateStr) => {
  if (!eventDateStr) return false;
  try {
    const eventDate = new Date(eventDateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate > now;
  } catch {
    return false;
  }
};

export const KegiatanDetail = React.memo(() => {
  const { slug, distance } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("map");
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  const fetchCollectionName = useCallback(async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);
      return eventDoc.exists() ? eventDoc.data().collectionName : null;
    } catch {
      return null;
    }
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", slug));
      if (! eventDoc.exists()) {
        setError("Event not found");
        setLoading(false);
        return null;
      }

      const eventData = { id: eventDoc.id, ...eventDoc.data() };
      setEvent(eventData);

      const eventStatus = eventData.status || "Registrasi Ditutup";
      const isEventOpen =
        eventStatus === "Registrasi Dibuka" ||
        eventStatus === "Registration Open";

      if (distance && eventData.categories) {
        const category = eventData.categories.find(
          (cat) => getCategorySlug(cat.categoryName) === distance
        );

        if (category) {
          const normalizedCategoryName = category.categoryName. trim();
          setSelectedCategory({
            ... category,
            categoryName: normalizedCategoryName,
            foreignerPrice: category.foreignerPrice || category.price,
            isRegistrationOpen: isEventOpen && isEventInFuture(category. date),
          });
        } else {
          setError(`Category for ${distance} not found`);
          console.error("[ERROR] Category not found for distance:", distance);
        }
      } else {
        setError("Invalid distance parameter");
        console.error("[ERROR] Invalid distance parameter:", distance);
      }

      setLoading(false);
      return eventData;
    } catch (err) {
      setError(err.message);
      console. error("[ERROR] Error fetching event data:", err);
      setLoading(false);
      return null;
    }
  }, [slug, distance]);

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };

      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);

        if (! querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                  data.orderId
                }`,
                { params: { eventId } }
              );
              const updatedStatus = response.data.transaction_status;
              if (updatedStatus !== status) {
                if (
                  updatedStatus === "pending" ||
                  updatedStatus === "settlement"
                ) {
                  await updateDoc(doc.ref, {
                    transaction_status: updatedStatus,
                  });
                  setTransactionStatus(updatedStatus);
                  return {
                    isRegistered: true,
                    status: updatedStatus,
                    token: data.snapToken,
                  };
                } else {
                  await deleteDoc(doc.ref);
                  setRegistrationStatus("not_registered");
                  setTransactionStatus(null);
                  setSnapToken(null);
                  return { isRegistered: false, status: null, token: null };
                }
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          } else {
            await deleteDoc(doc.ref);
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch {
        setError("Failed to check registration status.  Please try again.");
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (! eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const [fetchedCollectionName, registrationData] = await Promise.all([
            fetchCollectionName(eventData.eventId),
            user
              ? checkRegistration(null, eventData.eventId)
              : Promise.resolve({
                  isRegistered: false,
                  status: null,
                  token: null,
                }),
          ]);

          if (mounted) {
            setCollectionName(fetchedCollectionName);
            if (fetchedCollectionName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                fetchedCollectionName,
                eventData.eventId
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            }
          }
        } catch {
          if (mounted) setCollectionName(null);
        }
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, fetchCollectionName, checkRegistration, user]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (! isSnapReady || !window.snap) {
      showNotification("error", "Midtrans is not ready.  Please try again.");
      return;
    }

    if (! snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }

    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onPending: async () => {
          showNotification("warning", "Payment is being processed.");
          const { status } = await checkRegistration(
            collectionName,
            event. eventId
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onError: () => {
          showNotification("error", "Payment failed.  Please try again.");
        },
        onClose: () => {},
      });
    } catch {
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category. price == null && event.price == null) ||
        ! event.eventId
      ) {
        showNotification(
          "error",
          "Event data is incomplete. Please try again."
        );
        return;
      }

      if (! user) {
        showNotification("error", "Please login first to register.");
        navigate("/login");
        return;
      }

      if (! collectionName) {
        showNotification(
          "error",
          "Failed to load event data. Please try again."
        );
        return;
      }

      if (! category.isRegistrationOpen) {
        showNotification("error", "Registration for this category is closed.");
        return;
      }

      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path = `/form/${event.slug}`;

      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement") {
          showNotification("info", "You are already registered for this event.");
        } else if (transactionStatus === "pending") {
          handleBayar();
        }
        return;
      }

      navigate(path, {
        state: {
          title,
          price: category.price ??  event.price ??  0,
          foreignerPrice:
            category.foreignerPrice ?? category.price ?? event.price ?? 0,
          eventId: event.eventId,
          categoryName: category.categoryName,
        },
      });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl, selectedCategory, event) => {
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document. createElement("a");
        a. href = url;
        const fileName =
          event. type === "seminar"
            ?  `${event.title. toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase(). replace(/\s+/g, "-")}-${
                selectedCategory. distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    [showNotification]
  );

  const isRegistrationOpen = useMemo(() => {
    const eventStatus = event?.status || "Registrasi Ditutup";
    const isEventOpen =
      eventStatus === "Registrasi Dibuka" ||
      eventStatus === "Registration Open";
    return isEventOpen && isEventInFuture(selectedCategory?.date);
  }, [event, selectedCategory]);

  const getEventIcon = (type) => {
    switch (type?. toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error || !event || !selectedCategory) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-slate-100">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-lime-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-lime-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {error || "NOT FOUND"}
        </motion.p>
        <Link to="/kegiatan" className="text-lime-600 underline">
          Back to Events
        </Link>
      </section>
    );
  }

  const categoryIcons = getCategoryIcons(
    selectedCategory. categoryName,
    selectedCategory. distance
  );

  return (
    <section className="w-full min-h-screen text-lime-600 relative bg-white">
      <div
        className="relative w-full h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${bghero11})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 flex flex-col items-center justify-center gap-6">
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <FontAwesomeIcon
              icon={getEventIcon(event.type). icon}
              className={`text-white ${getEventIcon(event.type).size} z-10`}
              aria-label={`Icon for ${
                event.type === "trail_running" ? "Trail Running" : "Seminar"
              }`}
            />
          </motion.div>
          <div className="z-0 text-center">
            <motion.h1
              className="text-3xl md:text-5xl font-black uppercase text-white px-4"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {selectedCategory.categoryName || event.title}
            </motion.h1>
            <motion.div
              className="flex justify-center gap-4 mt-6"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: [0.4,0, 0.2, 1],
              }}
            >
              {categoryIcons.icon1Url && (
                <img
                  src={categoryIcons.icon1Url}
                  alt="UTMB Icon"
                  className="h-6 object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {categoryIcons.icon2Url && (
                <img
                  src={categoryIcons.icon2Url}
                  alt="UTMB Icon"
                  className="h-6 object-contain"
                  onError={(e) => {
                    e.target. style.display = "none";
                  }}
                />
              )}
              {categoryIcons. icon3Url && (
                <img
                  src={categoryIcons.icon3Url}
                  alt="ITRA Icon"
                  className="h-6 object-contain"
                  onError={(e) => {
                    e.target. style.display = "none";
                  }}
                />
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="space-y-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {(event. type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-2xl text-lime-600"
                  />
                  <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                    Date
                  </span>
                  <div className="text-sm text-gray-700">
                    {formatDate(selectedCategory.date)}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-2xl text-lime-600"
                  />
                  <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                    Start Time
                  </span>
                  <div className="text-sm text-gray-700">
                    {selectedCategory.time || "-"}
                  </div>
                </div>
                {event.type === "trail_running" && (
                  <>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faRuler}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Distance
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.distance != null
                          ? `${selectedCategory.distance} KM`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faMountain}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Elevation Gain
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.elevationGain != null
                          ?  `${selectedCategory.elevationGain} M`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faHourglassHalf}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Cut Off
                      </span>
                      <div className="text-sm text-gray-700">
                        {selectedCategory.cot != null
                          ? `${selectedCategory.cot} Hours`
                          : "-"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                      <FontAwesomeIcon
                        icon={faDollarSign}
                        className="text-2xl text-lime-600"
                      />
                      <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                        Fee
                      </span>
                      <div className="text-sm font-semibold text-lime-700 text-center">
                        -
                      </div>
                    </div>
                  </>
                )}
                {event.type === "seminar" && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
                    <FontAwesomeIcon
                      icon={faDollarSign}
                      className="text-2xl text-lime-600"
                    />
                    <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                      Fee
                    </span>
                    <div className="text-sm font-semibold text-lime-700 text-center">
                      -
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <RaceInfo categoryName={selectedCategory.categoryName} />

          <div>
            <div className="mt-6">
              {registrationStatus === "registered" &&
              transactionStatus === "settlement" ?  (
                <span className="w-full text-center uppercase px-6 py-4 block bg-lime-600 text-white rounded-xl">
                  Already Registered
                </span>
              ) : registrationStatus === "registered" &&
                transactionStatus === "pending" ? (
                <button
                  onClick={handleBayar}
                  className="w-full text-center uppercase px-6 py-4 block bg-lime-600 text-white rounded-xl hover:bg-lime-800 transition-colors duration-300"
                  disabled={! isSnapReady || ! snapToken}
                >
                  Complete Payment
                </button>
              ) : (
                <button
                  onClick={() => handleRegister(selectedCategory)}
                  className={`w-full text-center uppercase px-6 py-4 block rounded-xl transition-colors duration-300 ${
                    isRegistrationOpen
                      ? "bg-lime-600 text-white hover:bg-lime-800"
                      : "bg-slate-400 text-white opacity-50 cursor-not-allowed"
                  }`}
                  disabled={! isRegistrationOpen}
                >
                  {isRegistrationOpen ? "Register Now" : "Registration Closed"}
                </button>
              )}
            </div>
          </div>

          {event.type === "trail_running" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="mb-4">
                <div className="hidden md:flex space-x-0 rounded-t-xl overflow-hidden">
                  <button
                    className={`flex-1 text-center uppercase px-4 py-3 transition-colors duration-300 ${
                      viewMode === "map"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } ${viewMode === "map" ?  "rounded-tl-xl" : ""}`}
                    onClick={() => setViewMode("map")}
                  >
                    Map & GPX
                  </button>
                  <button
                    className={`flex-1 text-center uppercase px-4 py-3 transition-colors duration-300 ${
                      viewMode === "rundown"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    Rundown
                  </button>
                  <button
                    className={`flex-1 text-center uppercase px-4 py-3 transition-colors duration-300 ${
                      viewMode === "mandatory"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } ${viewMode === "mandatory" ?  "rounded-tr-xl" : ""}`}
                    onClick={() => setViewMode("mandatory")}
                  >
                    Mandatory Gears
                  </button>
                </div>
                <div className="md:hidden flex flex-col divide-y divide-gray-300 border-y border-gray-300">
                  <div className="relative">
                    <button
                      className={`w-full uppercase px-4 py-4 block transition-colors duration-300 ${
                        viewMode === "map"
                          ? "bg-lime-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setViewMode("map")}
                    >
                      Map & GPX
                    </button>
                  </div>
                  <button
                    className={`w-full uppercase px-4 py-4 block transition-colors duration-300 ${
                      viewMode === "rundown"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    Rundown
                  </button>
                  <button
                    className={`w-full uppercase px-4 py-4 block transition-colors duration-300 ${
                      viewMode === "mandatory"
                        ? "bg-lime-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("mandatory")}
                  >
                    Mandatory Gears
                  </button>
                </div>
              </div>
              {viewMode === "map" && selectedCategory.mapUrl && (
                <div className="pb-4">
                  <div className="relative w-full h-[600px] overflow-hidden mt-4">
                    <Iframe
                      url={selectedCategory.mapUrl}
                      width="100%"
                      height="600px"
                      display="block"
                      position="relative"
                    />
                  </div>
                <p className="text-lg px-4 leading-relaxed text-gray-700 text-center mb-4">
                    {selectedCategory.description ||
                      "No description available. "}
                  </p>
               {/*    {selectedCategory.gpxFileUrl && (
                    <div className="text-center px-4">
                      <button
                        onClick={() =>
                          handleExportGpx(
                            selectedCategory.gpxFileUrl,
                            selectedCategory,
                            event
                          )
                        }
                        className="inline-flex items-center gap-2 px-6 py-3 bg-lime-600 text-white rounded-xl hover:bg-lime-700 transition-colors duration-300 uppercase font-medium"
                        title="Download GPX file"
                      >
                        <FontAwesomeIcon
                          icon={faFileDownload}
                          className="text-sm"
                        />
                        Download GPX
                      </button>
                    </div>
                  )} */}
                </div>
              )}
              {viewMode === "rundown" && (
                <Rundown
                  categoryName={selectedCategory.categoryName}
                  rundown={selectedCategory.rundown}
                />
              )}
              {viewMode === "mandatory" && (
                <div>
                  <MandatoryGears
                    categoryName={selectedCategory.categoryName}
                  />
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
});

KegiatanDetail.displayName = "KegiatanDetail";

export default KegiatanDetail;